import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtInt, fmtNum, networkLabel } from "../lib/api";
import PageHeader from "../components/PageHeader";
import KpiCard from "../components/KpiCard";
import AdsetView from "../components/AdsetView";
import CopyButton from "../components/CopyButton";
import BudgetQuickPicker from "../components/BudgetQuickPicker";
import RecentBudgetBadge from "../components/RecentBudgetBadge";
import SpendDeltaBadge from "../components/SpendDeltaBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, ChevronRight, ChevronDown, Search,
  ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  Tooltip, CartesianGrid,
} from "recharts";

const RANGE_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "custom", label: "Custom" },
];

const COLUMNS = [
  { key: "campaign_name", label: "Campaign", align: "left", sortable: true },
  { key: "ad_group", label: "Ad set / group", align: "left", sortable: false },
  { key: "network", label: "Net", align: "left", sortable: true },
  { key: "status", label: "Status", align: "left", sortable: true },
  { key: "daily_budget", label: "Budget/d", align: "right", sortable: true },
  { key: "spend", label: "Spend", align: "right", sortable: true },
  { key: "platform_purchase_appts", label: "Purch.", title: "Purchase appointments", align: "right", sortable: true },
  { key: "platform_purchase_cpa", label: "£/purch.", title: "Cost per purchase appointment", align: "right", sortable: true },
  { key: "platform_browse_appts", label: "Brows.", title: "Browsing appointments", align: "right", sortable: true },
  { key: "platform_browse_cpa", label: "£/brows.", title: "Cost per browsing appointment", align: "right", sortable: true },
  { key: "platform_total_appts", label: "Total", title: "Total appointments (purchase + browsing)", align: "right", sortable: true },
  { key: "platform_total_cpa", label: "£/total", title: "Cost per total appointment", align: "right", sortable: true },
  { key: "orders", label: "TM Reg.", align: "right", sortable: true },
  { key: "cpa", label: "TM CPA", align: "right", sortable: true },
  { key: "variance_pct", label: "Var. %", align: "right", sortable: true },
  { key: "actions", label: "Quick", align: "right", sortable: false },
];

const computeVariance = (tm, plat) => {
  if (!plat || plat <= 0) return null;
  return ((Number(tm || 0) - Number(plat)) / Number(plat)) * 100;
};

function AdsetSubrow({ campaignId, campaignName, network, days, startDate, endDate }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState("");
  const [subFilter, setSubFilter] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const params = { network, name: campaignName };
    if (days === "custom") {
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
    } else if (days) {
      params.days = days;
    }
    api.get(`/campaigns/${campaignId}/adsets`, { params })
      .then((r) => {
        if (!mounted) return;
        setItems(r.data.items || []);
        setInfo(r.data.info || "");
      })
      .catch(() => mounted && setItems([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [campaignId, campaignName, network, days, startDate, endDate]);

  if (loading) {
    return (
      <tr className="bg-zinc-50">
        <td colSpan={18} className="px-5 py-3 text-xs text-zinc-500 font-mono">
          Loading {network === "meta" ? "ad sets" : "ad groups"}…
        </td>
      </tr>
    );
  }
  if (!items || items.length === 0) {
    return (
      <tr className="bg-zinc-50">
        <td colSpan={18} className="px-5 py-3 text-xs text-zinc-500 font-mono">
          {info || `No ${network === "meta" ? "ad sets" : "ad groups"} returned.`}
        </td>
      </tr>
    );
  }
  // Avg CPA across items for color coding (use TM conversions)
  const avgCpa = (() => {
    const pos = (items || []).filter((a) => (a.cpa || 0) > 0);
    if (!pos.length) return 0;
    return pos.reduce((s, a) => s + a.cpa, 0) / pos.length;
  })();
  const cpaColor = (v) => {
    if (!v || v <= 0) return "";
    if (avgCpa && v < avgCpa) return "bg-emerald-50 text-emerald-700";
    if (avgCpa && v > avgCpa * 1.5) return "bg-red-50 text-red-700";
    return "";
  };
  const label = network === "meta" ? "ad sets" : "ad groups";
  const fq = subFilter.trim().toLowerCase();
  const shown = fq
    ? items.filter((ad) => (ad.name || "").toLowerCase().includes(fq) || String(ad.id || "").toLowerCase().includes(fq))
    : items;
  return (
    <tr className="bg-zinc-50">
      <td colSpan={18} className="px-0 py-0">
        <div className="border-l-2 border-zinc-900 ml-5">
          <div className="px-3 py-2">
            <div className="relative w-full md:w-72">
              <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                data-testid={`adset-filter-${campaignId}`}
                value={subFilter}
                onChange={(e) => setSubFilter(e.target.value)}
                placeholder={`Filter ${label}…`}
                className="rounded-none border-zinc-300 pl-8 h-8 font-mono text-xs"
              />
            </div>
          </div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-zinc-100">
                <th className="text-left px-3 py-2 data-label">{network === "meta" ? "Ad Set" : "Ad Group"}</th>
                <th className="text-left px-3 py-2 data-label">Status</th>
                <th className="text-right px-3 py-2 data-label">Budget/d</th>
                <th className="text-right px-3 py-2 data-label">Spend</th>
                <th className="text-right px-3 py-2 data-label">Plat. Reg.</th>
                <th className="text-right px-3 py-2 data-label">Plat. CPA</th>
                <th className="text-right px-3 py-2 data-label">TM Reg.</th>
                <th className="text-right px-3 py-2 data-label">TM CPA</th>
                <th className="text-right px-3 py-2 data-label">Var. %</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-3 text-zinc-500 font-mono text-xs">No {label} match “{subFilter}”.</td>
                </tr>
              )}
              {shown.map((ad) => {
                const platReg = ad.meta_registrations || 0;
                const platCpa = ad.meta_cpa || 0;
                const tmReg = ad.conversions || 0;
                const tmCpa = ad.cpa || 0;
                const variance = platReg > 0 ? ((tmReg - platReg) / platReg) * 100 : null;
                return (
                <tr key={ad.id} className="border-b border-zinc-100">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <div className="font-medium">{ad.name}</div>
                      <CopyButton value={ad.name} label="Ad set name" testid={`copy-adsubrow-name-${ad.id}`} size={10} />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="text-zinc-500 font-mono text-[10px]">ID {ad.id}</div>
                      <CopyButton value={ad.id} label="Ad set ID" testid={`copy-adsubrow-id-${ad.id}`} size={9} />
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono uppercase text-[10px]">{ad.effective_status || ad.status}</td>
                  <td className="px-3 py-2 text-right font-mono">{ad.daily_budget != null ? fmtMoney(ad.daily_budget) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMoney(ad.spend)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(platReg, 1)}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {platCpa > 0 ? <span className={`px-1.5 py-0.5 ${cpaColor(platCpa)}`}>{fmtMoney(platCpa)}</span> : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(tmReg, 1)}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {tmCpa > 0 ? <span className={`px-1.5 py-0.5 ${cpaColor(tmCpa)}`}>{fmtMoney(tmCpa)}</span> : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {variance == null ? <span className="text-zinc-400">—</span> : (
                      <span className={`px-1 py-0.5 text-[10px] ${
                        Math.abs(variance) >= 25 ? "bg-red-50 text-red-700"
                        : Math.abs(variance) >= 10 ? "bg-amber-50 text-amber-700"
                        : "text-zinc-600"
                      }`}>{variance > 0 ? "+" : ""}{fmtNum(variance, 0)}%</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState("campaign"); // 'campaign' | 'adset'
  const [days, setDays] = useState("7");
  const [startDate, setStart] = useState("");
  const [endDate, setEnd] = useState("");
  const [network, setNetwork] = useState("all");
  const [accountId, setAccountId] = useState("all");
  const [accountIds, setAccountIds] = useState([]); // multi-select; empty = all
  const [accounts, setAccounts] = useState([]);
  const [minSpend, setMinSpend] = useState("0");
  const [search, setSearch] = useState(searchParams.get("campaign") || "");
  const [hideBrand, setHideBrand] = useState(false);

  // sort state: { key, dir: 'asc'|'desc' }
  const [sortKey, setSortKey] = useState("cpa");
  const [sortDir, setSortDir] = useState("asc");

  const [campaigns, setCampaigns] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [quickBusyId, setQuickBusyId] = useState(null);

  // When network changes, drop selected accounts not in the new network
  useEffect(() => {
    if (network === "all") return;
    setAccountIds((ids) => ids.filter((id) => accounts.find((a) => a.account_id === id && a.network === network)));
  }, [network, accounts]);

  // If arriving with ?campaign=<name>, switch to Campaign view & pre-fill the search filter.
  useEffect(() => {
    const c = searchParams.get("campaign");
    if (c) {
      setSearch(c);
      setView("campaign");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleAccounts = useMemo(() => {
    if (network === "all") return accounts;
    return accounts.filter((a) => a.network === network);
  }, [accounts, network]);

  const handleQuickBudget = async (campaign, direction, pct = 20) => {
    setQuickBusyId(`${campaign.campaign_id}-${direction}-${pct}`);
    try {
      // Use resolved_id when available (Google TM short-ids don't match Google Ads campaign IDs)
      const targetId = campaign.resolved_id || campaign.campaign_id;
      const r = await api.post(`/campaigns/${targetId}/quick-budget`, {
        direction,
        pct,
        name: campaign.campaign_name,
        network: campaign.network,
        customer_id: campaign.customer_id || undefined,
        budget_id: campaign.budget_id || undefined,
      });
      toast.success(
        `${direction === "increase" ? "+" : "−"}${pct}% on ${campaign.campaign_name.slice(0, 40)}`,
        { description: `£${r.data.current_budget.toFixed(2)} → £${r.data.new_budget.toFixed(2)}` }
      );
      // Optimistic update: reflect the new budget immediately in the row
      setCampaigns((list) => list.map((c) => (
        c.campaign_id === campaign.campaign_id
          ? {
              ...c,
              daily_budget: r.data.new_budget,
              budget_changed_at: new Date().toISOString(),
              budget_changed_from: r.data.current_budget,
              budget_changed_to: r.data.new_budget,
              budget_change_pct: pct,
              budget_change_direction: direction,
            }
          : c
      )));
    } catch (e) {
      toast.error("Budget update failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setQuickBusyId(null);
    }
  };

  const queryParams = useMemo(() => {
    const p = { min_spend: minSpend || 0 };
    if (days === "custom") {
      if (startDate) p.start_date = startDate;
      if (endDate) p.end_date = endDate;
    } else {
      p.days = days;
    }
    if (network !== "all") p.network = network;
    if (accountIds.length > 0) p.account_ids = accountIds.join(",");
    return p;
  }, [days, startDate, endDate, network, accountIds, minSpend]);

  // Ref to the current load() invocation token. Older inflight requests are dropped on response.
  const loadTokenRef = React.useRef(0);
  const loadAbortRef = React.useRef(null);

  const load = async () => {
    // Cancel any in-flight previous request and bump the token so its response is ignored.
    if (loadAbortRef.current) {
      try { loadAbortRef.current.abort(); } catch { /* noop */ }
    }
    const ctrl = new AbortController();
    loadAbortRef.current = ctrl;
    const myToken = ++loadTokenRef.current;
    setLoading(true);
    try {
      // Use Promise.allSettled so a single failing endpoint (e.g. TM rate-limit)
      // doesn't blank the whole dashboard — show what loaded, toast the rest.
      const [campRes, kpiRes, trendRes] = await Promise.allSettled([
        api.get("/campaigns", { params: { ...queryParams, sort: "cpa_asc" }, signal: ctrl.signal }),
        api.get("/kpis", { params: queryParams, signal: ctrl.signal }),
        api.get("/trends/cpa", {
          params: {
            ...queryParams,
            days: days === "custom" ? 30 : Number(days),
          },
          signal: ctrl.signal,
        }),
      ]);
      if (myToken !== loadTokenRef.current) return;

      const errors = [];
      if (campRes.status === "fulfilled") {
        setCampaigns(campRes.value.data.campaigns || []);
      } else if (!isCanceled(campRes.reason)) {
        errors.push(`Campaigns: ${detailOf(campRes.reason)}`);
      }
      if (kpiRes.status === "fulfilled") {
        setKpis(kpiRes.value.data);
      } else if (!isCanceled(kpiRes.reason)) {
        errors.push(`KPIs: ${detailOf(kpiRes.reason)}`);
      }
      if (trendRes.status === "fulfilled") {
        setTrend(trendRes.value.data);
      } else if (!isCanceled(trendRes.reason)) {
        errors.push(`Trend chart: ${detailOf(trendRes.reason)}`);
      }
      if (errors.length) {
        toast.error("Some data failed to load", { description: errors.join(" · "), duration: 8000 });
      }
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || e?.message === "canceled") return;
      if (myToken !== loadTokenRef.current) return;
      toast.error("Failed to load campaign data", { description: e.response?.data?.detail || e.message });
    } finally {
      if (myToken === loadTokenRef.current) setLoading(false);
    }
  };

  // Helpers for partial-failure handling
  const isCanceled = (err) =>
    err?.name === "CanceledError" || err?.code === "ERR_CANCELED" || err?.message === "canceled";
  const detailOf = (err) => err?.response?.data?.detail || err?.message || "unknown error";

  useEffect(() => {
    api.get("/accounts").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [JSON.stringify(queryParams)]);

  // Client-side filter + sort (operates on the already-fetched campaigns)
  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Tokenize on whitespace so URL params that decoded `+` to space still match
    // a campaign name that contained `+`. All tokens must appear in the name.
    const tokens = q ? q.split(/\s+/).filter(Boolean) : [];
    let list = campaigns.map((c) => ({
      ...c,
      variance_pct: computeVariance(c.orders, c.platform_orders),
    }));
    if (tokens.length) {
      list = list.filter((c) => {
        const name = (c.campaign_name || "").toLowerCase();
        const labels = (c.labels || []).join(" ").toLowerCase();
        const id = String(c.campaign_id || "").toLowerCase();
        return tokens.every((t) => name.includes(t) || labels.includes(t) || id.includes(t));
      });
    }
    // "Campaign does not include Brand" — hide branded campaigns (demand
    // harvesting, not acquisition) from the table and the totals footer.
    if (hideBrand) {
      list = list.filter((c) => !(c.campaign_name || "").toLowerCase().includes("brand"));
    }
    const arr = [...list];
    arr.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      // For CPA: push 0/missing rows to the bottom regardless of direction
      if (sortKey === "cpa") {
        const aNull = !(av > 0);
        const bNull = !(bv > 0);
        if (aNull && bNull) return 0;
        if (aNull) return 1;
        if (bNull) return -1;
      }
      if (typeof av === "string" || typeof bv === "string") {
        av = (av || "").toString().toLowerCase();
        bv = (bv || "").toString().toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      }
      const an = Number(av || 0); const bn = Number(bv || 0);
      return sortDir === "asc" ? an - bn : bn - an;
    });
    return arr;
  }, [campaigns, search, sortKey, sortDir, hideBrand]);

  // Column totals for the currently filtered/sorted campaign list. Counts are
  // summed; cost-per columns are blended (total spend ÷ total of that metric)
  // so the footer stays internally consistent with the visible Spend column.
  const totals = useMemo(() => {
    const t = { count: filteredSorted.length, daily_budget: 0, spend: 0,
      purchase: 0, browse: 0, total: 0, orders: 0 };
    for (const c of filteredSorted) {
      t.daily_budget += Number(c.daily_budget || 0);
      t.spend += Number(c.spend || 0);
      t.purchase += Number(c.platform_purchase_appts || 0);
      t.browse += Number(c.platform_browse_appts || 0);
      t.total += Number(c.platform_total_appts || 0);
      t.orders += Number(c.orders || 0);
    }
    return t;
  }, [filteredSorted]);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "cpa" || key === "campaign_name" || key === "network" || key === "status" ? "asc" : "desc");
    }
  };

  const sortIcon = (key) => {
    if (key !== sortKey) return <ArrowUpDown className="w-3 h-3 inline opacity-30" />;
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 inline" />
      : <ArrowDown className="w-3 h-3 inline" />;
  };

  return (
    <div data-testid="dashboard-page" className="fade-in">
      <PageHeader
        eyebrow="Performance Control Room"
        title="Campaigns Dashboard"
        description="Live data from ThoughtMetric. Toggle between campaign-level and ad-set-level views. Click the ±% picker on any row to adjust budgets by any percentage."
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex border border-zinc-300 bg-white" data-testid="view-toggle">
              <button
                data-testid="view-campaign"
                onClick={() => setView("campaign")}
                className={`px-4 h-9 text-xs font-mono uppercase tracking-wider ${
                  view === "campaign" ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-50"
                }`}>
                Campaign
              </button>
              <button
                data-testid="view-adset"
                onClick={() => setView("adset")}
                className={`px-4 h-9 text-xs font-mono uppercase tracking-wider border-l border-zinc-300 ${
                  view === "adset" ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-50"
                }`}>
                Ad set / Ad group
              </button>
            </div>
            <Button
              data-testid="dashboard-refresh-btn"
              onClick={load}
              disabled={loading}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-wider text-xs"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        }
      />

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 space-y-8">
        {/* Filters */}
        <div className="bg-white border border-zinc-200 p-5">
          <div className="data-label mb-3">Filters</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div>
              <label className="data-label block mb-1">Date range</label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger data-testid="filter-daterange" className="rounded-none border-zinc-300 font-mono text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`daterange-opt-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {days === "custom" && (
              <>
                <div>
                  <label className="data-label block mb-1">Start</label>
                  <Input data-testid="filter-start-date" type="date" value={startDate} onChange={(e) => setStart(e.target.value)} className="rounded-none border-zinc-300 font-mono text-sm" />
                </div>
                <div>
                  <label className="data-label block mb-1">End</label>
                  <Input data-testid="filter-end-date" type="date" value={endDate} onChange={(e) => setEnd(e.target.value)} className="rounded-none border-zinc-300 font-mono text-sm" />
                </div>
              </>
            )}
            <div>
              <label className="data-label block mb-1">Network</label>
              <Select value={network} onValueChange={setNetwork}>
                <SelectTrigger data-testid="filter-network" className="rounded-none border-zinc-300 font-mono text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All networks</SelectItem>
                  <SelectItem value="meta">Meta</SelectItem>
                  <SelectItem value="google">Google</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-2">
              <label className="data-label block mb-1">Ad accounts ({accountIds.length === 0 ? "all" : accountIds.length}/{visibleAccounts.length})</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="filter-account" variant="outline" className="rounded-none border-zinc-300 font-mono text-sm w-full justify-between">
                    <span className="truncate text-left">
                      {accountIds.length === 0
                        ? "All accounts"
                        : accountIds.length === 1
                        ? (accounts.find((a) => a.account_id === accountIds[0])?.name || accountIds[0])
                        : `${accountIds.length} accounts`}
                    </span>
                    <ChevronDown className="w-4 h-4 ml-2 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-96 rounded-none p-0 border-zinc-300">
                  <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between">
                    <div className="data-label">{visibleAccounts.length} accounts</div>
                    <div className="flex gap-2 text-[10px] font-mono uppercase">
                      <button onClick={() => setAccountIds(visibleAccounts.map((a) => a.account_id))} data-testid="dash-acct-all" className="hover:underline">all</button>
                      <span className="text-zinc-300">·</span>
                      <button onClick={() => setAccountIds([])} data-testid="dash-acct-none" className="hover:underline">none</button>
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {visibleAccounts.map((a) => (
                      <label key={a.account_id} className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 cursor-pointer">
                        <Checkbox checked={accountIds.includes(a.account_id)} onCheckedChange={() => {
                          setAccountIds((s) => s.includes(a.account_id) ? s.filter((x) => x !== a.account_id) : [...s, a.account_id]);
                        }} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm truncate">{a.name}</div>
                          <div className="text-xs text-zinc-500 font-mono">{a.account_id} · {networkLabel(a.network)} · {a.currency || "—"}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="data-label block mb-1">Min spend</label>
              <Input data-testid="filter-min-spend" type="number" value={minSpend} onChange={(e) => setMinSpend(e.target.value)} className="rounded-none border-zinc-300 font-mono text-sm" />
            </div>
          </div>
        </div>

        {/* KPI Bento */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard testid="kpi-spend" label="Total spend" value={fmtMoney(kpis?.total_spend || 0)} />
          <KpiCard testid="kpi-orders" label="Registrations" value={fmtInt(kpis?.total_orders || 0)} />
          <KpiCard testid="kpi-avg-cpa" label="Avg CPA" value={fmtMoney(kpis?.avg_cpa || 0)} />
          <KpiCard testid="kpi-best-cpa" label="Best CPA" value={fmtMoney(kpis?.best_cpa || 0)} accent="text-emerald-700" />
          <KpiCard testid="kpi-worst-cpa" label="Worst CPA" value={fmtMoney(kpis?.worst_cpa || 0)} accent="text-red-700" />
          <KpiCard testid="kpi-campaigns" label="Campaigns" value={fmtInt(kpis?.campaign_count || 0)} sub={`${kpis?.active_count || 0} active`} />
        </div>

        {/* CPA Trend Chart */}
        {trend && trend.series && trend.series.length > 0 && (
          <div className="bg-white border border-zinc-200 p-5" data-testid="cpa-trend-chart">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="data-label">CPA & Spend Trend</div>
                <div className="text-xs text-zinc-500 font-mono mt-1">
                  Daily spend (bars) + CPA (line) · last {trend.days} days · store-wide
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs font-mono text-zinc-600">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-zinc-300 inline-block"/>Spend</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-600 inline-block"/>CPA</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={trend.series} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#a1a1aa" />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#a1a1aa"
                       tickFormatter={(v) => `£${v}`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#2563eb"
                       tickFormatter={(v) => `£${v}`} />
                <Tooltip
                  contentStyle={{ background: "#18181b", color: "#fff", border: "none", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  labelStyle={{ color: "#a1a1aa" }}
                  formatter={(v, name) => name === "CPA" ? `£${Number(v).toFixed(2)}` : `£${Number(v).toFixed(2)}`}
                />
                <Bar yAxisId="left" dataKey="spend" name="Spend" fill="#d4d4d8" />
                <Line yAxisId="right" type="monotone" dataKey="cpa" name="CPA" stroke="#2563eb" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Table */}
        {view === "adset" ? (
          <AdsetView
            accounts={accounts}
            network={network}
            accountId={accountId}
            accountIds={accountIds}
            days={days}
            startDate={startDate}
            endDate={endDate}
            minSpend={minSpend}
            search={search}
          />
        ) : (
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="data-label">Campaigns</div>
              <div className="font-mono text-sm text-zinc-600 mt-1">
                {loading ? "Loading…" : `${filteredSorted.length} of ${campaigns.length} results`}
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="hide-brand-toggle" title="Hide campaigns whose name contains &quot;Brand&quot;">
                <Checkbox checked={hideBrand} onCheckedChange={(v) => setHideBrand(!!v)} />
                <span className="data-label text-zinc-600">Exclude Brand</span>
              </label>
              <div className="relative w-full md:w-80">
                <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="campaign-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter campaigns or labels…"
                  className="rounded-none border-zinc-300 pl-9 font-mono text-sm"
                />
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table data-testid="campaigns-table" className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-zinc-50">
                  <th className="text-left px-2 py-2 data-label w-6"></th>
                  <th className="text-left px-2 py-2 data-label w-8">#</th>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      data-testid={`th-${col.key}`}
                      title={col.title || col.label}
                      className={`px-2 py-2 data-label select-none ${col.align === "right" ? "text-right" : "text-left"} ${col.sortable ? "cursor-pointer hover:text-zinc-900" : ""}`}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                        {col.label}
                        {col.sortable && sortIcon(col.key)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredSorted.length === 0 && !loading && (
                  <tr>
                    <td colSpan={18} className="text-center py-12 text-zinc-500 font-mono text-sm">
                      No campaigns match.
                    </td>
                  </tr>
                )}
                {filteredSorted.map((c, idx) => {
                  const isOpen = !!expanded[c.campaign_id];
                  return (
                    <React.Fragment key={c.campaign_id}>
                      <tr
                        data-testid={`campaign-row-${c.campaign_id}`}
                        className="border-b border-zinc-100 hover:bg-zinc-50"
                      >
                        <td className="px-2 py-2">
                          <button
                            data-testid={`expand-${c.campaign_id}`}
                            onClick={() => setExpanded((e) => ({ ...e, [c.campaign_id]: !e[c.campaign_id] }))}
                            className="text-zinc-400 hover:text-zinc-900"
                          >
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          </button>
                        </td>
                        <td className="px-2 py-2 font-mono text-zinc-400 text-[10px]">
                          {String(idx + 1).padStart(2, "0")}
                        </td>
                        <td className="px-2 py-2 max-w-[180px]">
                          <div className="flex items-center gap-1 min-w-0">
                            <div className="font-medium text-zinc-900 truncate" title={c.campaign_name}>{c.campaign_name}</div>
                            <CopyButton value={c.campaign_name} label="Campaign name" testid={`copy-name-${c.campaign_id}`} size={11} />
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="text-[10px] text-zinc-500 font-mono">ID {c.campaign_id}</div>
                            <CopyButton value={c.campaign_id} label="Campaign ID" testid={`copy-id-${c.campaign_id}`} size={9} />
                          </div>
                        </td>
                        <td className="px-2 py-2 text-[11px] text-zinc-500">
                          <button
                            onClick={() => setExpanded((e) => ({ ...e, [c.campaign_id]: !e[c.campaign_id] }))}
                            className="underline-offset-2 hover:underline font-mono"
                          >
                            {isOpen ? "Hide" : "View"}
                          </button>
                        </td>
                        <td className="px-2 py-2">
                          <Badge variant="outline" className="rounded-none font-mono uppercase text-[10px] border-zinc-300 px-1">
                            {networkLabel(c.network)}
                          </Badge>
                        </td>
                        <td className="px-2 py-2">
                          <span className={`px-1.5 py-0.5 text-[10px] font-mono border ${
                            (c.status || "").toUpperCase() === "PAUSED"
                              ? "bg-zinc-100 text-zinc-600 border-zinc-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"
                          }`}>
                            {c.status || "—"}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">
                          {c.daily_budget != null ? (
                            <div className="inline-flex items-center gap-1 justify-end">
                              <span className="text-zinc-900">{fmtMoney(c.daily_budget)}</span>
                              <RecentBudgetBadge
                                changedAt={c.budget_changed_at}
                                from={c.budget_changed_from}
                                to={c.budget_changed_to}
                                pct={c.budget_change_pct}
                                direction={c.budget_change_direction}
                                testid={`recent-budget-${c.campaign_id}`}
                              />
                            </div>
                          ) : (
                            <span className="text-zinc-400">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">
                          <span className="inline-flex items-center justify-end">
                            {fmtMoney(c.spend)}
                            <SpendDeltaBadge tm={c.spend} platform={c.platform_spend} testid={`spend-delta-${c.campaign_id}`} />
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap text-zinc-600">{fmtNum(c.platform_purchase_appts, 1)}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap text-zinc-600">{c.platform_purchase_cpa > 0 ? fmtMoney(c.platform_purchase_cpa) : "—"}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap text-zinc-600">{fmtNum(c.platform_browse_appts, 1)}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap text-zinc-600">{c.platform_browse_cpa > 0 ? fmtMoney(c.platform_browse_cpa) : "—"}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap text-zinc-600">{fmtNum(c.platform_total_appts, 1)}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap text-zinc-600">{c.platform_total_cpa > 0 ? fmtMoney(c.platform_total_cpa) : "—"}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtNum(c.orders, 1)}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">
                          <span className={`px-1.5 py-0.5 ${
                            c.cpa > 0 && c.cpa < (kpis?.avg_cpa || Infinity)
                              ? "bg-emerald-50 text-emerald-700"
                              : c.cpa > (kpis?.avg_cpa || 0) * 1.5
                              ? "bg-red-50 text-red-700"
                              : ""
                          }`}>
                            {fmtMoney(c.cpa)}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">
                          {c.variance_pct == null ? (
                            <span className="text-zinc-400">—</span>
                          ) : (
                            <span
                              title={`TM ${fmtNum(c.orders,1)} vs Platform ${fmtNum(c.platform_orders,1)}`}
                              className={`px-1.5 py-0.5 ${
                                Math.abs(c.variance_pct) >= 25
                                  ? "bg-red-50 text-red-700"
                                  : Math.abs(c.variance_pct) >= 10
                                  ? "bg-amber-50 text-amber-700"
                                  : "text-zinc-600"
                              }`}>
                              {c.variance_pct > 0 ? "+" : ""}{fmtNum(c.variance_pct, 0)}%
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {c.daily_budget != null ? (
                            <BudgetQuickPicker
                              testid={`quick-budget-${c.campaign_id}`}
                              currentBudget={c.daily_budget}
                              busy={quickBusyId?.startsWith(`${c.campaign_id}-`)}
                              defaultDirection={
                                c.cpa > (kpis?.avg_cpa || 0) * 1.5 && c.cpa > 0
                                  ? "decrease"
                                  : "increase"
                              }
                              onApply={({ direction, pct }) => handleQuickBudget(c, direction, pct)}
                            />
                          ) : (
                            <span className="text-[9px] font-mono uppercase text-zinc-400">
                              {c.budget_level === "adset" ? "ad-set" : c.budget_level === "lifetime" ? "lifetime" : "—"}
                            </span>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <AdsetSubrow campaignId={c.campaign_id} campaignName={c.campaign_name} network={c.network} days={days} startDate={startDate} endDate={endDate} />
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {filteredSorted.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold text-zinc-900" data-testid="campaigns-total-row">
                    <td className="px-2 py-2"></td>
                    <td className="px-2 py-2"></td>
                    <td className="px-2 py-2 data-label whitespace-nowrap">TOTAL · {totals.count} campaign{totals.count === 1 ? "" : "s"}</td>
                    <td className="px-2 py-2"></td>
                    <td className="px-2 py-2"></td>
                    <td className="px-2 py-2"></td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{totals.daily_budget > 0 ? fmtMoney(totals.daily_budget) : "—"}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtMoney(totals.spend)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtNum(totals.purchase, 1)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{totals.purchase > 0 ? fmtMoney(totals.spend / totals.purchase) : "—"}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtNum(totals.browse, 1)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{totals.browse > 0 ? fmtMoney(totals.spend / totals.browse) : "—"}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtNum(totals.total, 1)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{totals.total > 0 ? fmtMoney(totals.spend / totals.total) : "—"}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtNum(totals.orders, 1)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{totals.orders > 0 ? fmtMoney(totals.spend / totals.orders) : "—"}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums whitespace-nowrap">{(() => {
                      const v = computeVariance(totals.orders, totals.total);
                      return v == null ? "—" : `${v > 0 ? "+" : ""}${fmtNum(v, 0)}%`;
                    })()}</td>
                    <td className="px-2 py-2"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
